import React, { useState } from 'react';
import { AlertCircle, User, Calendar, FileText } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { ApprovalRequest } from '../../../services/approvalService';
import { format } from 'date-fns';

interface ApprovalDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  approvalRequest: ApprovalRequest | null;
  action: 'approve' | 'reject' | 'view';
  onConfirm: (comments: string) => Promise<void>;
  loading?: boolean;
}

const ApprovalDetailsDrawer: React.FC<ApprovalDetailsDrawerProps> = ({
  open,
  onClose,
  approvalRequest,
  action,
  onConfirm,
  loading = false,
}) => {
  const [comments, setComments] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setError(null);
      if (action === 'reject' && !comments.trim()) {
        setError('Please provide a reason for rejection');
        return;
      }
      await onConfirm(comments);
      setComments('');
      onClose();
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'An error occurred');
    }
  };

  const handleClose = () => {
    setComments('');
    setError(null);
    onClose();
  };

  if (!approvalRequest) return null;

  const formatResourceType = (resourceType: string) =>
    resourceType
      .split(/(?=[A-Z])/)
      .join(' ')
      .replace(/^\w/, (c) => c.toUpperCase());

  const statusSeverity = approvalRequest.status === 'pending' ? 'warning' : approvalRequest.status === 'approved' ? 'success' : 'danger';
  const actionLabels: Record<string, string> = {
    create: 'Create',
    update: 'Update',
    delete: 'Delete',
  };
  const actionSeverity: Record<string, 'success' | 'info' | 'danger' | 'secondary'> = {
    create: 'success',
    update: 'info',
    delete: 'danger',
  };

  const renderDataField = (key: string, value: unknown, level = 0): React.ReactNode => {
    const label = key.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase()).trim();
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <div key={key} className={level > 0 ? 'ml-4 mt-2' : 'mt-3'}>
          <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
          <div className="bg-gray-50 rounded-lg p-2.5 space-y-1.5">
            {Object.entries(value as Record<string, unknown>).map(([k, v]) => renderDataField(k, v, level + 1))}
          </div>
        </div>
      );
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      return (
        <div key={key} className="mt-3">
          <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
          <ul className="list-disc list-inside text-xs text-gray-800 space-y-0.5">
            {value.map((item, i) => (
              <li key={i}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</li>
            ))}
          </ul>
        </div>
      );
    }
    return (
      <div key={key} className={`flex items-start py-1.5 ${level === 0 ? 'border-b border-gray-100' : ''}`}>
        <span className="text-xs font-medium text-gray-600 min-w-[120px] shrink-0">{label}</span>
        <span className="text-xs text-gray-900 break-words">{String(value)}</span>
      </div>
    );
  };

  const headerTitle =
    action === 'view' ? 'Request details' : action === 'approve' ? 'Approve request' : 'Reject request';
  const headerSub = `${formatResourceType(approvalRequest.resourceType)} · ${approvalRequest.action}`;

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <Button
        label={action === 'view' ? 'Close' : 'Cancel'}
        className="p-button-outlined p-button-sm"
        disabled={loading}
        onClick={handleClose}
      />
      {action !== 'view' && approvalRequest.status === 'pending' && (
        <Button
          label={loading ? 'Processing…' : action === 'approve' ? 'Approve' : 'Reject'}
          icon={action === 'approve' ? 'pi pi-check' : 'pi pi-times'}
          className={action === 'approve' ? 'p-button-success p-button-sm' : 'p-button-danger p-button-sm'}
          disabled={loading}
          onClick={handleConfirm}
        />
      )}
    </div>
  );

  return (
    <Dialog
      visible={open}
      onHide={handleClose}
      header={
        <div className="flex flex-col gap-0.5">
          <span className="font-semibold text-gray-900">{headerTitle}</span>
          <span className="text-xs text-gray-500">{headerSub}</span>
        </div>
      }
      footer={footer}
      className="w-full max-w-lg"
      contentClassName="pt-2"
      blockScroll
      dismissableMask
    >
      <div className="space-y-4 font-[family-name:var(--font-outfit)]">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Tag value={approvalRequest.status} severity={statusSeverity} className="!text-xs" />
          <Tag value={actionLabels[approvalRequest.action] ?? approvalRequest.action} severity={actionSeverity[approvalRequest.action] ?? 'secondary'} className="!text-xs" />
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">Requester</h4>
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5 text-gray-500" />
              <span className="font-medium text-gray-900">{approvalRequest.requesterName}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <FileText className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs">{approvalRequest.requesterEmail}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs">
                {format(new Date(approvalRequest.createdAt), 'MMM d, yyyy · HH:mm')}
              </span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-gray-700 mb-2">Request data</h4>
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            {Object.entries(approvalRequest.requestData).map(([key, value]) => renderDataField(key, value))}
          </div>
        </div>

        {approvalRequest.originalData && Object.keys(approvalRequest.originalData).length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Original data (before changes)</h4>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              {Object.entries(approvalRequest.originalData).map(([key, value]) => renderDataField(key, value))}
            </div>
          </div>
        )}

        {approvalRequest.status !== 'pending' && (
          <div
            className={`rounded-lg p-3 ${
              approvalRequest.status === 'approved'
                ? 'bg-emerald-50 border border-emerald-200'
                : 'bg-red-50 border border-red-200'
            }`}
          >
            <h4
              className={`text-xs font-semibold mb-2 ${
                approvalRequest.status === 'approved' ? 'text-emerald-900' : 'text-red-900'
              }`}
            >
              {approvalRequest.status === 'approved' ? 'Approval' : 'Rejection'} information
            </h4>
            <div className="space-y-1.5 text-xs">
              {approvalRequest.approverName && (
                <div className="flex gap-2">
                  <span className="text-gray-600 shrink-0">By:</span>
                  <span className="text-gray-900">{approvalRequest.approverName}</span>
                </div>
              )}
              {(approvalRequest.approvedAt || approvalRequest.rejectedAt) && (
                <div className="flex gap-2">
                  <span className="text-gray-600 shrink-0">Date:</span>
                  <span className="text-gray-900">
                    {format(
                      new Date(approvalRequest.approvedAt || approvalRequest.rejectedAt || ''),
                      'MMM d, yyyy · HH:mm'
                    )}
                  </span>
                </div>
              )}
              {approvalRequest.approverComments && (
                <div className="flex gap-2">
                  <span className="text-gray-600 shrink-0">Comments:</span>
                  <span className="text-gray-900 flex-1">{approvalRequest.approverComments}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {action !== 'view' && approvalRequest.status === 'pending' && (
          <div>
            <label htmlFor="approval-comments" className="block text-xs font-medium text-gray-700 mb-1.5">
              {action === 'reject' ? 'Reason for rejection *' : 'Comments (optional)'}
            </label>
            <InputTextarea
              id="approval-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value ?? '')}
              rows={3}
              placeholder={
                action === 'reject'
                  ? 'Provide a reason for rejecting this request…'
                  : 'Add any comments…'
              }
              disabled={loading}
              className="w-full text-sm"
            />
            {action === 'reject' && (
              <p className="mt-1 text-[11px] text-gray-500">* Required for rejection</p>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ApprovalDetailsDrawer;
